import React, { useState, useEffect } from 'react';
import {
  FaSearch,
  FaEye,
  FaMoneyBill,
  FaCreditCard,
  FaFilter,
  FaChartBar,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaInfoCircle,
} from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
const REACT_APP_RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

function FrontdeskRoomservices() {
  const navigate = useNavigate();
  const [billingRecords, setBillingRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [selectedBilling, setSelectedBilling] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [orderDetails, setOrderDetails] = useState([]);
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);

  // Filter states
  const [dateFilter, setDateFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');

  // Summary stats
  const [summaryStats, setSummaryStats] = useState({
    totalRecords: 0,
    totalAmount: 0,
    totalTax: 0,
    totalBase: 0,
    uniqueBookings: 0,
    paidAmount: 0,
    pendingAmount: 0,
  });

  const itemsPerPage = 15;

  // Status options
  const statusOptions = ['Pending', 'In Progress', 'Completed', 'Cancelled'];

  // Load Razorpay script
  useEffect(() => {
    const loadRazorpayScript = () => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => setRazorpayLoaded(true);
      script.onerror = () => {
        toast.error('Error loading Razorpay script. Please try again.');
      };
      document.body.appendChild(script);
    };

    loadRazorpayScript();
  }, []);

  // Fetch billing summary data
  useEffect(() => {
    fetchBillingSummary();
    fetchAllOrders();
  }, []);

  // Apply filters whenever billingRecords or filter changes
  useEffect(() => {
    applyFilters();
  }, [billingRecords, dateFilter, startDate, endDate, searchQuery, paymentStatusFilter]);

  const fetchBillingSummary = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/room-service-requests/billing/all/summary?limit=1000&offset=0&sort_by=created_date&sort_order=desc`,
        {
          headers: { accept: 'application/json' },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setBillingRecords(data.billing_records || []);
        updateSummaryStats(data.billing_records || []);
      } else {
        console.error('Failed to fetch billing summary');
        toast.error('Failed to load billing data');
      }
    } catch (error) {
      console.error('Error fetching billing summary:', error);
      toast.error('Error loading billing data');
    } finally {
      setLoading(false);
    }
  };

  // Get actual payment status from payments array
  const getActualPaymentStatus = (record) => {
    // Check if there are any successful payments
    const successfulPayments =
      record.payments?.filter(
        (p) => p.payment_status === 'Success' || p.payment_status === 'Paid'
      ) || [];

    const totalPaid = successfulPayments.reduce((sum, p) => sum + p.amount, 0);

    if (totalPaid >= record.final_amount) {
      return {
        status: 'Paid',
        totalPaid,
        method: successfulPayments[0]?.payment_method || 'Unknown',
      };
    } else if (totalPaid > 0 && totalPaid < record.final_amount) {
      return {
        status: 'Partial',
        totalPaid,
        method: successfulPayments[0]?.payment_method || 'Unknown',
      };
    } else {
      return { status: 'Unpaid', totalPaid: 0, method: null };
    }
  };

  // Update summary statistics
  const updateSummaryStats = (records) => {
    const totalAmount = records.reduce((sum, r) => sum + (r.final_amount || 0), 0);
    const totalTax = records.reduce((sum, r) => sum + (r.tax_amount || 0), 0);
    const totalBase = records.reduce((sum, r) => sum + (r.base_amount || 0), 0);
    const uniqueBookings = new Set(records.map((r) => r.bookingid)).size;

    let paidAmount = 0;
    let pendingAmount = 0;

    records.forEach((record) => {
      const actualStatus = getActualPaymentStatus(record);
      if (actualStatus.status === 'Paid') {
        paidAmount += record.final_amount || 0;
      } else if (actualStatus.status === 'Partial') {
        paidAmount += actualStatus.totalPaid || 0;
        pendingAmount += record.final_amount - actualStatus.totalPaid || 0;
      } else {
        pendingAmount += record.final_amount || 0;
      }
    });

    setSummaryStats({
      totalRecords: records.length,
      totalAmount: Number(totalAmount.toFixed(2)), // Round to 2 decimals
      totalTax: Number(totalTax.toFixed(2)),
      totalBase: Number(totalBase.toFixed(2)),
      uniqueBookings,
      paidAmount: Number(paidAmount.toFixed(2)),
      pendingAmount: Number(pendingAmount.toFixed(2)),
    });
  };

  // Fetch all orders once
  const fetchAllOrders = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/room-service-requests/`, {
        headers: { accept: 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        setAllOrders(data);
      } else {
        console.error('Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  // Apply filters to records
  const applyFilters = () => {
    let records = [...billingRecords];

    // Search filter
    if (searchQuery.trim() !== '') {
      records = records.filter(
        (record) =>
          record.bookingid?.toString().includes(searchQuery) ||
          record.guest_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          record.room_number?.toString().includes(searchQuery) ||
          record.billingid?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          record.guest_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          record.guest_phone?.toString().includes(searchQuery)
      );
    }

    // Payment status filter based on actual status
    if (paymentStatusFilter !== 'all') {
      records = records.filter((record) => {
        const actualStatus = getActualPaymentStatus(record);
        if (paymentStatusFilter === 'paid') {
          return actualStatus.status === 'Paid';
        } else if (paymentStatusFilter === 'unpaid') {
          return actualStatus.status === 'Unpaid';
        } else if (paymentStatusFilter === 'partial') {
          return actualStatus.status === 'Partial';
        }
        return true;
      });
    }

    // Date filter
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const weekStart = new Date(today);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());

    records = records.filter((record) => {
      if (!record.created_date) return true;
      const recordDate = new Date(record.created_date);
      recordDate.setHours(0, 0, 0, 0);

      switch (dateFilter) {
        case 'today':
          return recordDate.getTime() === today.getTime();
        case 'yesterday':
          return recordDate.getTime() === yesterday.getTime();
        case 'this_week':
          return recordDate >= weekStart && recordDate <= today;
        case 'custom':
          if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            return recordDate >= start && recordDate <= end;
          }
          return true;
        default:
          return true;
      }
    });

    setFilteredRecords(records);
    setCurrentPage(1);
    updateSummaryStats(records);
  };

  // Reset filters
  const resetFilters = () => {
    setDateFilter('all');
    setStartDate('');
    setEndDate('');
    setSearchQuery('');
    setPaymentStatusFilter('all');
    setCurrentPage(1);
  };

  // Fetch order details for a specific booking
  const fetchOrderDetails = async (bookingId) => {
    setLoading(true);
    try {
      const filteredOrders = allOrders.filter(
        (order) => order.bookingid?.toString() === bookingId?.toString()
      );

      if (filteredOrders.length > 0) {
        setOrderDetails(filteredOrders);
        setSelectedBookingId(bookingId);
        setShowOrderDetailsModal(true);
      } else {
        const response = await fetch(
          `${BQ_BASE_URL}/bq/api/room-service-requests/?bookingid=${bookingId}`,
          {
            headers: { accept: 'application/json' },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            setOrderDetails(data);
            setSelectedBookingId(bookingId);
            setShowOrderDetailsModal(true);
          } else {
            toast.info('No orders found for this booking');
          }
        } else {
          toast.error('Failed to fetch order details');
        }
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast.error('Error loading order details');
    } finally {
      setLoading(false);
    }
  };

  // Handle status update
  const handleStatusUpdate = async (requestId, newStatus) => {
    setUpdatingId(requestId);

    const currentRequest = orderDetails.find((req) => req.id === requestId);

    if (!currentRequest) {
      setUpdatingId(null);
      return;
    }

    const updatedData = {
      ...currentRequest,
      status: newStatus,
    };

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/room-service-requests/${requestId}`, {
        method: 'PUT',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedData),
      });

      if (response.ok) {
        setOrderDetails((prevRequests) =>
          prevRequests.map((request) =>
            request.id === requestId ? { ...request, status: newStatus } : request
          )
        );
        setAllOrders((prevOrders) =>
          prevOrders.map((order) =>
            order.id === requestId ? { ...order, status: newStatus } : order
          )
        );
        toast.success('Status updated successfully');
      } else {
        console.error('Failed to update status');
        toast.error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error updating status');
    } finally {
      setUpdatingId(null);
    }
  };

  // Handle search
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentRecords = filteredRecords.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const getVisiblePages = () => {
    const pages = [];
    const pageGroupSize = 10;
    const currentGroup = Math.ceil(currentPage / pageGroupSize);
    const startPage = (currentGroup - 1) * pageGroupSize + 1;
    const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  // Get payment status badge
  const getPaymentStatusBadge = (record) => {
    const actualStatus = getActualPaymentStatus(record);

    if (actualStatus.status === 'Paid') {
      return (
        <div className="flex items-center gap-1">
          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
            <FaCheckCircle size={12} />
            Paid
          </span>
          {actualStatus.method && (
            <span className="text-xs text-gray-500">({actualStatus.method})</span>
          )}
        </div>
      );
    } else if (actualStatus.status === 'Partial') {
      return (
        <div className="flex items-center gap-1">
          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium flex items-center gap-1">
            <FaClock size={12} />
            Partial
          </span>
          <span className="text-xs text-gray-500">(₹{actualStatus.totalPaid} paid)</span>
        </div>
      );
    } else {
      return (
        <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium flex items-center gap-1">
          <FaTimesCircle size={12} />
          Unpaid
        </span>
      );
    }
  };

  // Check if payment is allowed
  const isPaymentAllowed = (record) => {
    const actualStatus = getActualPaymentStatus(record);
    return actualStatus.status !== 'Paid';
  };

  // Handle payment action
  const handlePaymentAction = (record, method) => {
    if (!isPaymentAllowed(record)) {
      toast.info('This billing is already fully paid');
      return;
    }
    setSelectedBilling(record);
    setSelectedPaymentMethod(method);
    setShowPaymentModal(true);
  };

  // Handle Cash Payment
  const handleCashPayment = async () => {
    if (!selectedBilling) {
      toast.error('No billing record selected');
      return;
    }

    const actualStatus = getActualPaymentStatus(selectedBilling);
    const remainingAmount = selectedBilling.final_amount - actualStatus.totalPaid;

    setIsProcessingPayment(true);
    try {
      const paymentData = {
        booking_id: selectedBilling.bookingid,
        billing_id: selectedBilling.billingid,
        amount: remainingAmount,
      };

      const response = await fetch(`${BQ_BASE_URL}/bq/api/payments/cash`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Cash payment successful!');
        setShowPaymentModal(false);
        setSelectedPaymentMethod(null);
        setSelectedBilling(null);
        fetchBillingSummary();
        fetchAllOrders();
      } else {
        throw new Error(data.detail || 'Payment failed');
      }
    } catch (error) {
      console.error('Cash payment error:', error);
      toast.error(error.message || 'Failed to process cash payment');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Handle Digital/Razorpay Payment
  const handleDigitalPayment = async () => {
    if (!selectedBilling) {
      toast.error('No billing record selected');
      return;
    }

    if (!razorpayLoaded) {
      toast.error('Razorpay script is not loaded yet. Please try again.');
      return;
    }

    setIsProcessingPayment(true);
    try {
      const apiUrl = `${BQ_BASE_URL}/bq/api/razorpay/create_payment_order?billingid=${selectedBilling.billingid}&bookingid=${selectedBilling.bookingid}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to create payment order');

      const orderData = await response.json();

      if (orderData && orderData.razorpay_order_id) {
        const options = {
          key: REACT_APP_RAZORPAY_KEY,
          amount: orderData.amount,
          currency: 'INR',
          name: 'Pagoda Hotel',
          description: `Room Service Payment - ${selectedBilling.billingid}`,
          order_id: orderData.razorpay_order_id,
          handler: async function (response) {
            const paymentDetails = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            };

            try {
              const verificationResponse = await fetch(
                `${BQ_BASE_URL}/bq/api/razorpay/verify_payment`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                  },
                  body: JSON.stringify(paymentDetails),
                }
              );

              if (verificationResponse.ok) {
                toast.success('Payment successful and verified!');
                setShowPaymentModal(false);
                setSelectedPaymentMethod(null);
                setSelectedBilling(null);
                fetchBillingSummary();
                fetchAllOrders();
              } else {
                toast.error('Payment verification failed.');
              }
            } catch (error) {
              console.error('Verification error:', error);
              toast.error('Error verifying payment. Please try again.');
            }
          },
          prefill: {
            name: selectedBilling.guest_name || 'Guest',
            email: selectedBilling.guest_email || '',
            contact: selectedBilling.guest_phone?.toString() || '',
          },
          theme: {
            color: '#3399cc',
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
        setShowPaymentModal(false);
        setSelectedPaymentMethod(null);
        setSelectedBilling(null);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Error processing payment. Please try again.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Payment Modal
  const PaymentModal = () => {
    if (!showPaymentModal || !selectedBilling) return null;

    const paymentMethodName = selectedPaymentMethod === 'cash' ? 'Cash' : 'Digital';
    const actualStatus = getActualPaymentStatus(selectedBilling);
    const remainingAmount = selectedBilling.final_amount - actualStatus.totalPaid;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-xl font-bold text-gray-800">Confirm Payment</h3>
            <button
              onClick={() => {
                setShowPaymentModal(false);
                setSelectedPaymentMethod(null);
                setSelectedBilling(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <span className="text-2xl">×</span>
            </button>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Booking ID</span>
              <span className="font-medium">{selectedBilling.bookingid}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Guest</span>
              <span className="font-medium">{selectedBilling.guest_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Room</span>
              <span className="font-medium">{selectedBilling.room_number}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total Amount</span>
              <span className="font-medium">₹{selectedBilling.final_amount}</span>
            </div>
            {actualStatus.totalPaid > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Already Paid</span>
                <span className="font-medium text-green-600">₹{actualStatus.totalPaid}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Payment Method</span>
              <span className="font-medium text-blue-600">{paymentMethodName}</span>
            </div>
            <div className="border-t pt-3">
              <div className="flex justify-between text-lg font-bold">
                <span>Amount to Pay</span>
                <span className="text-green-600">₹{remainingAmount}</span>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => {
                setShowPaymentModal(false);
                setSelectedPaymentMethod(null);
                setSelectedBilling(null);
              }}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={selectedPaymentMethod === 'cash' ? handleCashPayment : handleDigitalPayment}
              disabled={isProcessingPayment}
              className={`flex-1 px-4 py-2.5 text-white font-medium rounded-lg transition-colors ${
                selectedPaymentMethod === 'cash'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              } ${isProcessingPayment ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isProcessingPayment ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Processing...
                </span>
              ) : (
                `Pay ₹${remainingAmount}`
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Order Details Modal
  const OrderDetailsModal = () => {
    if (!showOrderDetailsModal) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-blue-50 to-purple-50">
            <div>
              <h3 className="text-xl font-bold text-gray-800">
                Order Details - Booking ID: {selectedBookingId}
              </h3>
              <p className="text-sm text-gray-600 mt-1">Total Orders: {orderDetails.length}</p>
            </div>
            <button
              onClick={() => {
                setShowOrderDetailsModal(false);
                setOrderDetails([]);
                setSelectedBookingId(null);
              }}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {orderDetails.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No orders found for this booking</div>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Item
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Qty
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Urgency
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Scheduled
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {orderDetails.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {order.item_name || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{order.category_name || 'N/A'}</td>
                      <td className="px-4 py-3 text-center">{order.quantity}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            order.urgencylevel === 'Urgent'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {order.urgencylevel}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {new Date(order.scheduledtime).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={order.status}
                          onChange={(e) => handleStatusUpdate(order.id, e.target.value)}
                          disabled={updatingId === order.id}
                          className={`px-3 py-1.5 border rounded-lg text-sm bg-white ${
                            order.status === 'Completed'
                              ? 'border-green-300 text-green-700'
                              : order.status === 'Cancelled'
                                ? 'border-red-300 text-red-700'
                                : 'border-gray-300'
                          }`}
                        >
                          {statusOptions.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                        {updatingId === order.id && (
                          <span className="ml-2 text-xs text-gray-500 animate-pulse">
                            Updating...
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {order.status !== 'Completed' && (
                          <button
                            onClick={() => handleStatusUpdate(order.id, 'Completed')}
                            className="px-3 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors"
                          >
                            Mark Done
                          </button>
                        )}
                        {order.status === 'Completed' && (
                          <span className="text-xs text-green-600 font-medium">✓ Completed</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Loader component
  const Loader = () => (
    <div className="flex justify-center items-center py-20">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen p-6 bg-gray-50">
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">P M S</h1>
        <div className="flex space-x-6">
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/front-desk-operations')
            }
            className="btn"
          >
            Front Desk Operations
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/guest-managment')
            }
            className="btn"
          >
            Guest Management
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/front-desk-roomservices')
            }
            className="btn text-black font-bold"
          >
            Hospitality Services
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/front-desk-amenities')
            }
            className="btn"
          >
            Facility Services
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/billing-payments')
            }
            className="btn"
          >
            Billing & Payments
          </button>
          <button
            onClick={() =>
              navigate('/dashboard/core-modules/property-management-system/refunds-adjustments')
            }
            className="btn"
          >
            Refunds & Adjustments
          </button>
        </div>
      </header>

      <div className="p-4 text-center">
        <h1 className="text-2xl font-bold text-center text-black">
          Room Service & Order Management
        </h1>
        <p className="text-sm font-poppins font-semibold text-center mb-1 text-gray-600">
          Effortlessly track and place room service orders for a seamless guest experience.
        </p>
      </div>

      {/* Summary Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 font-medium">Total Records</p>
              <p className="text-2xl font-bold text-gray-800">{summaryStats.totalRecords}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <FaChartBar className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 font-medium">Total Amount</p>
              <p className="text-2xl font-bold text-green-600">₹{summaryStats.totalAmount}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <FaMoneyBill className="text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 font-medium">Total Tax Amount</p>
              <p className="text-2xl font-bold text-purple-600">₹{summaryStats.totalTax}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <FaChartBar className="text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 font-medium">Unique Bookings</p>
              <p className="text-2xl font-bold text-orange-600">{summaryStats.uniqueBookings}</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-full">
              <FaChartBar className="text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="mb-4 mt-2 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[250px]">
          <input
            type="text"
            className="p-2 w-full border border-gray-300 rounded-lg pl-9 placeholder:text-gray-500"
            placeholder="Search by booking ID, guest name & room number"
            value={searchQuery}
            onChange={handleSearch}
          />
          <FaSearch className="absolute top-3 left-3 transform text-gray-500" />
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
        >
          <FaFilter />
          Filters
        </button>

        <Link
          to={
            '/dashboard/core-modules/property-management-system/front-desk-roomservices/room-services-booking'
          }
        >
          <button className="bg-black hover:bg-gray-800 text-white py-2 px-4 rounded-lg transition-colors">
            Book Room Services
          </button>
        </Link>

        <button
          onClick={() => {
            fetchBillingSummary();
            fetchAllOrders();
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow-md p-4 mb-4 border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date Filter</label>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg "
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this_week">This Week</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Status</label>
              <select
                value={paymentStatusFilter}
                onChange={(e) => setPaymentStatusFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg"
              >
                <option value="all">All</option>
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
                <option value="partial">Partial</option>
              </select>
            </div>

            {dateFilter === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg "
                  />
                </div>
              </>
            )}
          </div>

          <div className="mt-4 flex gap-3">
            <button
              onClick={applyFilters}
              className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors"
            >
              Apply Filters
            </button>
            <button
              onClick={resetFilters}
              className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Reset All
            </button>
          </div>
        </div>
      )}

      {/* Table of Billing Records */}
      <div className="overflow-x-auto rounded-lg shadow-lg bg-white">
        {loading ? (
          <Loader />
        ) : (
          <table className="min-w-full text-sm table-auto">
            <thead className="bg-gray-200 text-black text-left">
              <tr>
                <th className="px-2 py-3">Booking ID</th>
                <th className="px-2 py-3">Room</th>
                <th className="px-2 py-3">Guest</th>
                <th className="px-2 py-3">Check-in</th>
                <th className="px-2 py-3">Check-out</th>
                <th className="px-2 py-3">Base</th>
                <th className="px-2 py-3">Tax</th>
                <th className="px-2 py-3">Total</th>
                <th className="px-2 py-3">Paid</th>
                <th className="px-2 py-3">Status</th>
                <th className="px-2 py-3">Created</th>
                <th className="px-2 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentRecords.map((record, index) => {
                const actualStatus = getActualPaymentStatus(record);
                return (
                  <tr
                    key={record.billingid}
                    className={`border-b hover:bg-gray-100 ${index % 2 === 0 ? 'bg-gray-50' : ''}`}
                  >
                    <td className="px-2 py-3 text-xs">{record.bookingid}</td>
                    <td className="px-2 py-3">{record.room_number}</td>
                    <td className="px-2 py-3">{record.guest_name}</td>
                    <td className="px-2 py-3 text-xs">{record.checkin_date}</td>
                    <td className="px-2 py-3 text-xs">{record.checkout_date}</td>
                    <td className="px-2 py-3">₹{record.base_amount}</td>
                    <td className="px-2 py-3">₹{record.tax_amount}</td>
                    <td className="px-2 py-3 font-bold">₹{record.final_amount}</td>
                    <td className="px-2 py-3">₹{actualStatus.totalPaid}</td>
                    <td className="px-2 py-3">{getPaymentStatusBadge(record)}</td>
                    <td className="px-2 py-3 text-xs">
                      {new Date(record.created_date).toLocaleDateString()}
                    </td>
                    <td className="px-2 py-3">
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            fetchOrderDetails(record.bookingid);
                          }}
                          className="p-1.5 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors"
                          title="View Orders"
                        >
                          <FaEye size={14} />
                        </button>
                        {isPaymentAllowed(record) ? (
                          <>
                            <button
                              onClick={() => handlePaymentAction(record, 'cash')}
                              className="p-1.5 bg-green-100 text-green-600 rounded hover:bg-green-200 transition-colors"
                              title="Cash Payment"
                            >
                              <FaMoneyBill size={14} />
                            </button>
                            <button
                              onClick={() => handlePaymentAction(record, 'digital')}
                              className="p-1.5 bg-purple-100 text-purple-600 rounded hover:bg-purple-200 transition-colors"
                              title="Digital Payment"
                            >
                              <FaCreditCard size={14} />
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-green-600 font-medium px-2 py-1 flex items-center gap-1">
                            <FaCheckCircle size={14} />
                            Paid
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!loading && filteredRecords.length === 0 && (
          <div className="text-center py-8 text-gray-500">No billing records found</div>
        )}
      </div>

      {/* Pagination Controls */}
      {!loading && filteredRecords.length > 0 && (
        <div className="flex justify-center items-center mt-8 text-xs gap-2 flex-wrap">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className="px-3 py-2 bg-black text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-gray-800"
          >
            Previous
          </button>

          {currentPage > 10 && (
            <>
              <button
                onClick={() => handlePageChange(1)}
                className="px-3 py-2 bg-gray-300 text-black rounded-full hover:bg-gray-400 mx-1 min-w-[40px]"
              >
                1
              </button>
              <span className="px-2">...</span>
            </>
          )}

          {getVisiblePages().map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-2 py-2 mx-1 min-w-[30px] ${
                currentPage === page
                  ? 'bg-black text-white rounded-full'
                  : 'bg-gray-300 text-black rounded-full hover:bg-gray-400'
              }`}
            >
              {page}
            </button>
          ))}

          {currentPage < totalPages - 9 && (
            <>
              <span className="px-2">...</span>
              <button
                onClick={() => handlePageChange(totalPages)}
                className="px-3 py-2 bg-gray-300 text-black rounded-full hover:bg-gray-400 mx-1 min-w-[40px]"
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="px-3 py-2 bg-black text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-gray-800"
          >
            Next
          </button>
        </div>
      )}

      {/* Payment Modal */}
      <PaymentModal />

      {/* Order Details Modal */}
      <OrderDetailsModal />

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

export default FrontdeskRoomservices;

// import React, { useState, useEffect } from 'react';
// import { FaSearch, FaInfoCircle } from 'react-icons/fa';
// import { Link, useNavigate } from 'react-router-dom';
// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// function FrontdeskRoomservices() {
//   const navigate = useNavigate();
//   const [roomServiceRequests, setRoomServiceRequests] = useState([]);
//   const [searchQuery, setSearchQuery] = useState('');
//   const [currentPage, setCurrentPage] = useState(1);
//   const [loading, setLoading] = useState(true);
//   const [updatingId, setUpdatingId] = useState(null); // Track which row is being updated
//   const itemsPerPage = 15;

//   // Status options based on your database constraint
//   const statusOptions = ['Pending', 'In Progress', 'Completed', 'Cancelled'];

//   // Fetching room service requests data
//   useEffect(() => {
//     setLoading(true);
//     fetch(`${BQ_BASE_URL}/bq/api/room-service-requests/`)
//       .then((response) => response.json())
//       .then((data) => {
//         setRoomServiceRequests(data);
//         setLoading(false);
//       })
//       .catch((error) => {
//         console.error('Error fetching room service requests:', error);
//         setLoading(false);
//       });
//   }, []);

//   // Handle status update
//   const handleStatusUpdate = async (requestId, newStatus) => {
//     setUpdatingId(requestId);

//     // Find the current request to get its data
//     const currentRequest = roomServiceRequests.find((req) => req.id === requestId);

//     // Prepare the updated data
//     const updatedData = {
//       ...currentRequest,
//       status: newStatus,
//     };

//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/room-service-requests/${requestId}`, {
//         method: 'PUT',
//         headers: {
//           accept: 'application/json',
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify(updatedData),
//       });

//       if (response.ok) {
//         // Update local state
//         setRoomServiceRequests((prevRequests) =>
//           prevRequests.map((request) =>
//             request.id === requestId ? { ...request, status: newStatus } : request
//           )
//         );
//       } else {
//         console.error('Failed to update status');
//       }
//     } catch (error) {
//       console.error('Error updating status:', error);
//     } finally {
//       setUpdatingId(null);
//     }
//   };

//   // Handle search functionality
//   const handleSearch = (event) => {
//     setSearchQuery(event.target.value);
//     setCurrentPage(1);
//   };

//   // Filtered room service requests based on search query
//   const filteredRequests = roomServiceRequests.filter(
//     (request) =>
//       request.category_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
//       request.service_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
//       request.item_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
//       request.status?.toLowerCase().includes(searchQuery.toLowerCase()) ||
//       request.bookingid?.toString().includes(searchQuery)
//   );

//   // Pagination logic
//   const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
//   const startIndex = (currentPage - 1) * itemsPerPage;
//   const currentRequests = filteredRequests.slice(startIndex, startIndex + itemsPerPage);

//   const handlePageChange = (pageNumber) => {
//     setCurrentPage(pageNumber);
//   };

//   const handleNextPage = () => {
//     if (currentPage < totalPages) {
//       setCurrentPage(currentPage + 1);
//     }
//   };

//   const handlePreviousPage = () => {
//     if (currentPage > 1) {
//       setCurrentPage(currentPage - 1);
//     }
//   };

//   // Function to get visible page numbers (show 10 pages at a time)
//   const getVisiblePages = () => {
//     const pages = [];
//     const pageGroupSize = 10;
//     const currentGroup = Math.ceil(currentPage / pageGroupSize);
//     const startPage = (currentGroup - 1) * pageGroupSize + 1;
//     const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);

//     for (let i = startPage; i <= endPage; i++) {
//       pages.push(i);
//     }
//     return pages;
//   };

//   // Loader component
//   const Loader = () => (
//     <div className="flex justify-center items-center py-20">
//       <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
//     </div>
//   );

//   // Tooltip component for special instructions
//   const SpecialInstructionsCell = ({ instructions }) => {
//     const [showTooltip, setShowTooltip] = useState(false);

//     return (
//       <td className="px-2 py-3 relative">
//         <div
//           className="flex items-center cursor-help"
//           onMouseEnter={() => setShowTooltip(true)}
//           onMouseLeave={() => setShowTooltip(false)}
//         >
//           <span className="truncate max-w-[150px]">{instructions || 'No instructions'}</span>
//           {instructions && <FaInfoCircle className="ml-1 text-gray-400 flex-shrink-0" />}

//           {/* Tooltip */}
//           {showTooltip && instructions && (
//             <div className="absolute z-10 bg-gray-800 text-white text-sm rounded-lg p-2 max-w-xs whitespace-normal break-words shadow-lg left-0 top-full mt-1">
//               {instructions}
//             </div>
//           )}
//         </div>
//       </td>
//     );
//   };

//   return (
//     <div className="flex flex-col min-h-screen p-6 bg-gray-50">
//       <header className="flex justify-between items-center mb-3 bg-white p-3">
//         <h1 className="text-black text-xl font-bold ml-2">P M S</h1>
//         <div className="flex space-x-6">
//           <button
//             onClick={() =>
//               navigate('/dashboard/core-modules/property-management-system/front-desk-operations')
//             }
//             className="btn "
//           >
//             Front Desk Operations
//           </button>
//           <button
//             onClick={() =>
//               navigate('/dashboard/core-modules/property-management-system/guest-managment')
//             }
//             className="btn "
//           >
//             Guest Management
//           </button>
//           <button
//             onClick={() =>
//               navigate('/dashboard/core-modules/property-management-system/front-desk-roomservices')
//             }
//             className="btn text-black font-bold"
//           >
//             Hospitality Services
//           </button>
//           <button
//             onClick={() =>
//               navigate('/dashboard/core-modules/property-management-system/front-desk-amenities')
//             }
//             className="btn "
//           >
//             Facility Services
//           </button>
//           <button
//             onClick={() =>
//               navigate('/dashboard/core-modules/property-management-system/billing-payments')
//             }
//             className="btn  "
//           >
//             Billing & Payments
//           </button>
//           <button
//             onClick={() =>
//               navigate('/dashboard/core-modules/property-management-system/refunds-adjustments')
//             }
//             className="btn"
//           >
//             Refunds & Adjustments
//           </button>
//         </div>
//       </header>

//       <div className="p-4 text-center">
//         <h1 className="text-2xl font-bold text-center text-black">
//           Room Service & Order Management
//         </h1>
//         <p className="text-sm font-poppins font-semibold text-center mb-1 text-gray-600">
//           Effortlessly track and place room service orders for a seamless guest experience.
//         </p>
//       </div>

//       {/* Search Bar */}
//       <div className="mb-4 mt-6 flex items-center gap-3">
//         <div className="relative">
//           <input
//             type="text"
//             className="p-2 w-[500px] border border-gray-300 rounded-lg pl-9 placeholder:text-gray-500"
//             placeholder="Search by reservation number or category name"
//             value={searchQuery}
//             onChange={handleSearch}
//           />
//           <FaSearch className="absolute top-3 left-3 transform text-gray-500" />
//         </div>

//         <Link
//           to={
//             '/dashboard/core-modules/property-management-system/front-desk-roomservices/room-services-booking'
//           }
//         >
//           <button className="bg-black hover:text-white text-white py-2 px-4 rounded-lg">
//             Book Room Services
//           </button>
//         </Link>
//       </div>

//       {/* Table of Room Service Requests */}
//       <div className="overflow-x-auto rounded-lg shadow-lg bg-white">
//         {loading ? (
//           <Loader />
//         ) : (
//           <table className="min-w-full text-sm table-auto">
//             <thead className="bg-gray-200 text-black">
//               <tr>
//                 <th className="px-2 py-3">Reservation no.</th>
//                 {/* <th className="px-2 py-3">Category</th>
//                 <th className="px-2 py-3">Service</th> */}
//                 <th className="px-2 py-3">Item</th>
//                 <th className="px-2 py-3">Quantity</th>
//                 <th className="px-2 py-3">Urgency Level</th>
//                 <th className="px-2 py-3">Created At</th>
//                 <th className="px-2 py-3">Scheduled Time</th>
//                 <th className="px-2 py-3">Requested Time</th>
//                 <th className="px-2 py-3">Special Instructions</th>
//                 <th className="px-2 py-3">Status</th>
//                 <th className="px-2 py-3">Total Amount</th>
//               </tr>
//             </thead>
//             <tbody>
//               {currentRequests.map((request, index) => (
//                 <tr
//                   key={request.id}
//                   className={`border-b hover:bg-gray-100 ${index % 2 === 0 ? 'bg-gray-50' : ''}`}
//                 >
//                   <td className="px-2 py-3">{request.bookingid}</td>
//                   {/* <td className="px-2 py-3">{request.category_name || "N/A"}</td>
//                   <td className="px-2 py-3">{request.service_name || "N/A"}</td> */}
//                   <td className="px-2 py-3">{request.item_name || 'N/A'}</td>
//                   <td className="px-2 py-3">{request.quantity}</td>
//                   <td className="px-2 py-3">{request.urgencylevel}</td>
//                   <td className="px-2 py-3">{new Date(request.createdat).toLocaleString()}</td>
//                   <td className="px-2 py-3">{new Date(request.scheduledtime).toLocaleString()}</td>
//                   <td className="px-2 py-3">{new Date(request.requestedtime).toLocaleString()}</td>

//                   {/* Special Instructions with Tooltip */}
//                   <SpecialInstructionsCell instructions={request.specialinstructions} />

//                   {/* Status Dropdown */}
//                   <td className="px-2 py-3">
//                     <select
//                       value={request.status}
//                       onChange={(e) => handleStatusUpdate(request.id, e.target.value)}
//                       disabled={updatingId === request.id}
//                       className={`p-1 border rounded ${
//                         request.status === 'Pending'
//                           ? 'bg-gray-100'
//                           : request.status === 'In Progress'
//                             ? 'bg-gray-100'
//                             : request.status === 'Completed'
//                               ? 'bg-gray-100'
//                               : request.status === 'Cancelled'
//                                 ? 'bg-gray-100'
//                                 : 'bg-gray-100'
//                       }`}
//                     >
//                       {statusOptions.map((status) => (
//                         <option key={status} value={status}>
//                           {status}
//                         </option>
//                       ))}
//                     </select>
//                     {updatingId === request.id && (
//                       <span className="ml-2 text-xs text-gray-500">Updating...</span>
//                     )}
//                   </td>

//                   <td className="px-2 py-3">₹{request.totalamount}</td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         )}
//       </div>

//       {/* Pagination Controls */}
//       {!loading && filteredRequests.length > 0 && (
//         <div className="flex justify-center items-center mt-8 text-xs gap-2">
//           {/* Previous Button */}
//           <button
//             onClick={handlePreviousPage}
//             disabled={currentPage === 1}
//             className="px-3 py-2 bg-black text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-gray-800"
//           >
//             Previous
//           </button>

//           {/* Show first page and ellipsis if not in first group */}
//           {currentPage > 10 && (
//             <>
//               <button
//                 onClick={() => handlePageChange(1)}
//                 className="px-3 py-2 bg-gray-300 text-black rounded-full hover:bg-gray-400 mx-1 min-w-[40px]"
//               >
//                 1
//               </button>
//               <span className="px-2">...</span>
//             </>
//           )}

//           {/* Page Numbers - Show 10 pages at a time */}
//           {getVisiblePages().map((page) => (
//             <button
//               key={page}
//               onClick={() => handlePageChange(page)}
//               className={`px-2 py-2 mx-1 min-w-[30px] ${
//                 currentPage === page
//                   ? 'bg-black text-white rounded-full'
//                   : 'bg-gray-300 text-black rounded-full hover:bg-gray-400'
//               }`}
//             >
//               {page}
//             </button>
//           ))}

//           {/* Show last page and ellipsis if not in last group */}
//           {currentPage < totalPages - 9 && (
//             <>
//               <span className="px-2">...</span>
//               <button
//                 onClick={() => handlePageChange(totalPages)}
//                 className="px-3 py-2 bg-gray-300 text-black rounded-full hover:bg-gray-400 mx-1 min-w-[40px]"
//               >
//                 {totalPages}
//               </button>
//             </>
//           )}

//           {/* Next Button */}
//           <button
//             onClick={handleNextPage}
//             disabled={currentPage === totalPages}
//             className="px-3 py-2 bg-black text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-gray-800"
//           >
//             Next
//           </button>
//         </div>
//       )}
//     </div>
//   );
// }

// export default FrontdeskRoomservices;
